import { List } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId, ProjectMember } from 'api/models';
import { Project } from 'api/project';
import { Caption01, ProductiveHeading07 } from 'components/styles';
import { useGetAllMembersInProject } from 'hooks/use-idb';
import { useMemberships } from 'hooks/use-memberships';
import { useUserProjectPermissions } from 'hooks/use-user-project-permissions';
import { MemberType } from '../../project-settings';
import { ListItem } from './members-list-item';

const EmptyContainer = styled.div`
  ${Caption01};
  color: var(--gray-50);
  text-align: center;
  padding: var(--spacing-06) 0;
`;
const Header = styled.div`
  padding: var(--spacing-03) var(--spacing-05);
  background-color: var(--coolest-gray-20);
  ${ProductiveHeading07};
  color: var(--gray-80);
  position: sticky;
  top: 0;
  z-index: 1;
  span {
    color: var(--gray-50);
    font-weight: var(--font-weight-normal);
  }
`;

interface Props {
  memberType: MemberType;
  project: Project;
}
export const MembersList: FC<Props> = ({ project, memberType }) => {
  const { projectId } = project;
  const [status] = useMemberships(ProjectId(projectId));
  const { data: inProjectUsers } = useGetAllMembersInProject({
    projectId: project.projectId,
  });
  const projectMembers = useMemo(
    () =>
      inProjectUsers.filter(
        ({ company: { companyId: userCompanyId } }) =>
          userCompanyId === memberType.company.companyId,
      ),
    [inProjectUsers, memberType.company.companyId],
  );
  const loggedInUserPermissions = useUserProjectPermissions(projectId);

  return (
    <>
      <Header>
        {memberType.company.companyName} <span>({projectMembers.length})</span>
      </Header>
      {projectMembers.length > 0 || status === 'loading' ? (
        <List
          itemLayout="horizontal"
          dataSource={projectMembers}
          loading={status === 'loading'}
          renderItem={(memberDetails: ProjectMember) => (
            <ListItem
              member={memberDetails}
              project={project}
              loggedInUserPermissions={loggedInUserPermissions}
            />
          )}
        />
      ) : (
        <EmptyContainer>
          <FormattedMessage
            id={`project.invitation.empty_table.${memberType.userType.toLowerCase()}`}
          />
        </EmptyContainer>
      )}
    </>
  );
};
